import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Tabs,
  Tab,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControlLabel,
  Switch,
  Divider,
  Grid,
  Paper,
  IconButton,
  Avatar,
  FormHelperText
} from '@mui/material';
import { 
  offerStandaloneService, 
  type OfferStandalone, 
  type CreateOfferStandaloneRequest 
} from '../../services/offerStandaloneService';
import { useNotification } from '../layouts/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import userService, { AppUser } from '../../services/userService';

interface AddOfferStandaloneFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (offer: OfferStandalone) => void;
  isEditMode?: boolean;
  initialData?: OfferStandalone;
}

const AddOfferStandaloneForm: React.FC<AddOfferStandaloneFormProps> = ({
  open,
  onClose,
  onAdd,
  isEditMode = false,
  initialData
}) => {
  // Stan formularza
  const [formData, setFormData] = useState<CreateOfferStandaloneRequest>({
    offer_preparer: '',
    client_name: '',
    client_address: '',
    order_name: '',
    location: '',
    investment_address: '',
    site_inspection: 'Nie',
    site_inspection_date: '',
    submission_deadline: '',
    estimated_order_value: undefined,
    bid_bond: undefined,
    status: 'Prekwalifikacja',
    client_website: '',
    miro_link: '',
    technical_experience: '',
    personnel_potential: '',
    economic_situation: ''
  });

  // Stan dla Google Drive Link
  const [googleDriveLink, setGoogleDriveLink] = useState<string>('');
  const [generatingFolder, setGeneratingFolder] = useState(false);

  // Stan dla upload plików
  const [clientFormsFiles, setClientFormsFiles] = useState<File[]>([]);
  const [specificationFiles, setSpecificationFiles] = useState<File[]>([]);
  
  // Stan dla UI
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tabValue, setTabValue] = useState(0);
  const { showNotification } = useNotification();
  const { user } = useAuth();

  // Lista użytkowników do pola PO
  const [users, setUsers] = useState<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url'>[]>([]);

  useEffect(() => {
    // Ładuj użytkowników do dropdownu PO
    userService.listUsersPublicForBusinessForms().then(setUsers).catch(() => {});
  }, []);

  // Reset formularza przy otwieraniu/zamykaniu
  useEffect(() => {
    if (open) {
      if ((isEditMode || initialData) && initialData) {
        setFormData({
          offer_preparer: initialData.offer_preparer || '',
          client_name: initialData.client_name || '',
          client_address: initialData.client_address || '',
          order_name: initialData.order_name || '',
          location: initialData.location || '',
          investment_address: initialData.investment_address || '',
          site_inspection: initialData.site_inspection || 'Nie',
          site_inspection_date: initialData.site_inspection_date || '',
          submission_deadline: initialData.submission_deadline || '',
          estimated_order_value: initialData.estimated_order_value,
          bid_bond: initialData.bid_bond,
          status: initialData.status || 'Prekwalifikacja',
          client_website: initialData.client_website || '',
          miro_link: initialData.miro_link || '',
          technical_experience: initialData.technical_experience || '',
          personnel_potential: initialData.personnel_potential || '',
          economic_situation: initialData.economic_situation || ''
        });
        setGoogleDriveLink(initialData.google_drive_link || '');
      } else {
        // Automatycznie ustaw zalogowanego użytkownika jako PO
        const defaultOfferPreparer = user?.user_id || '';
        setFormData({
          offer_preparer: defaultOfferPreparer,
          client_name: '',
          client_address: '',
          order_name: '',
          location: '',
          investment_address: '',
          site_inspection: 'Nie',
          site_inspection_date: '',
          submission_deadline: '',
          estimated_order_value: undefined,
          bid_bond: undefined,
          status: 'Prekwalifikacja',
          client_website: '',
          miro_link: '',
          technical_experience: '',
          personnel_potential: '',
          economic_situation: ''
        });
        setGoogleDriveLink('');
      }
      setErrors({});
      setClientFormsFiles([]);
      setSpecificationFiles([]);
    }
  }, [open, isEditMode, initialData, user]);

  // Walidacja formularza
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.offer_preparer) {
      newErrors.offer_preparer = 'Przygotowuje ofertę jest wymagane';
    }

    if (!formData.status) {
      newErrors.status = 'Status jest wymagany';
    }

    if (!formData.client_name.trim()) {
      newErrors.client_name = 'Zamawiający jest wymagany';
    }

    if (!formData.order_name.trim()) {
      newErrors.order_name = 'Nazwa zamówienia jest wymagana';
    }

    if (!formData.location || !formData.location.trim()) {
      newErrors.location = 'Miejscowość jest wymagana';
    }

    if (!formData.site_inspection) {
      newErrors.site_inspection = 'Wizja lokalna jest wymagana';
    }

    if (formData.site_inspection === 'Tak' && !formData.site_inspection_date) {
      newErrors.site_inspection_date = 'Termin wizji lokalnej jest wymagany';
    }

    if (!formData.submission_deadline) {
      newErrors.submission_deadline = 'Termin składania oferty jest wymagany';
    }

    if (formData.bid_bond === undefined || formData.bid_bond === null) {
      newErrors.bid_bond = 'Wadium jest wymagane';
    }

    if (!formData.technical_experience || !formData.technical_experience.trim()) {
      newErrors.technical_experience = 'Doświadczenie techniczne jest wymagane';
    }

    if (!formData.personnel_potential || !formData.personnel_potential.trim()) {
      newErrors.personnel_potential = 'Potencjał osobowy jest wymagany';
    }

    if (!formData.economic_situation || !formData.economic_situation.trim()) {
      newErrors.economic_situation = 'Sytuacja ekonomiczna jest wymagana';
    }

    if (!formData.client_website || !formData.client_website.trim()) {
      newErrors.client_website = 'Strona zamawiającego jest wymagana';
    }

    // Sprawdź czy data wizji lokalnej (jeśli podana) nie jest po terminie składania oferty
    if (formData.site_inspection === 'Tak' && formData.site_inspection_date && formData.submission_deadline) {
      const siteDate = new Date(formData.site_inspection_date);
      const submissionDate = new Date(formData.submission_deadline);
      if (siteDate >= submissionDate) {
        newErrors.site_inspection_date = 'Data wizji lokalnej musi być przed terminem składania oferty';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Obsługa zmian w polach formularza
  const handleInputChange = (field: keyof CreateOfferStandaloneRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Wyczyść błąd dla tego pola
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Obsługa generowania folderu Google Drive
  const handleGenerateFolder = async () => {
    if (!isEditMode || !initialData) {
      showNotification('Funkcja dostępna tylko w trybie edycji', 'warning');
      return;
    }

    try {
      setGeneratingFolder(true);
      const result = await offerStandaloneService.generateDriveFolder(initialData.id);
      setGoogleDriveLink(result.google_drive_link);
      showNotification('Folder Google Drive został utworzony pomyślnie', 'success');
      
      // Aktualizuj dane w bazie
      const updatedOffer = await offerStandaloneService.updateOffer(initialData.id, {});
      onAdd(updatedOffer);
      
    } catch (error: any) {
      console.error('Błąd podczas generowania folderu:', error);
      showNotification(
        error.message || 'Wystąpił błąd podczas tworzenia folderu Google Drive', 
        'error'
      );
    } finally {
      setGeneratingFolder(false);
    }
  };

  // Obsługa przesyłania plików
  const handleFileChange = (type: 'client-forms' | 'specification', files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      if (type === 'client-forms') {
        setClientFormsFiles(fileArray);
      } else {
        setSpecificationFiles(fileArray);
      }
    }
  };

  // Style zgodne z dokumentacją formularza "Dodaj projekt"
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: '#2D2D32'
    },
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    // Usuń czerwoną ramkę dla error state - tylko czerwony tekst helperText
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    },
    '& .MuiFormHelperText-root': { 
      ml: 0,
      color: '#d32f2f' // Zawsze czerwony dla wymaganych pól
    }
  } as const;

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    // Gdy select jest otwarty (expanded), ramka ma kolor #2020AF
    '&:has(.MuiSelect-root[aria-expanded="true"]) .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '16px',
      color: '#2D2D32'
    }
  } as const;

  const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
      {text}
      {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
    </Typography>
  );

  // Obsługa wysłania formularza
  const handleSubmit = async () => {
    console.log('handleSubmit called');
    console.log('Form data:', formData);
    
    if (!validateForm()) {
      console.log('Validation failed, errors:', errors);
      return;
    }

    console.log('Validation passed, proceeding with submit');
    try {
      setLoading(true);

      let result: OfferStandalone;

      if (isEditMode && initialData) {
        // Edycja istniejącej oferty
        console.log('Updating offer with data:', formData);
        console.log('client_address:', formData.client_address);
        console.log('investment_address:', formData.investment_address);
        result = await offerStandaloneService.updateOffer(initialData.id, formData);
        
        // Jeśli w trybie edycji nie ma google_drive_link, a pola są wypełnione, spróbuj wygenerować folder
        if (!result.google_drive_link && formData.client_name && formData.order_name) {
          try {
            const folderResult = await offerStandaloneService.generateDriveFolder(result.id);
            result.google_drive_link = folderResult.google_drive_link;
            showNotification('Folder Google Drive został utworzony automatycznie', 'success');
          } catch (error) {
            console.warn('Nie udało się automatycznie utworzyć folderu Google Drive:', error);
          }
        }
      } else {
        // Tworzenie nowej oferty
        result = await offerStandaloneService.createOffer(formData);
      }

      // Upload plików jeśli zostały wybrane
      if (clientFormsFiles.length > 0) {
        try {
          await offerStandaloneService.uploadClientForms(result.id, clientFormsFiles);
          showNotification('Formularze zamawiającego zostały przesłane pomyślnie', 'success');
        } catch (error) {
          console.error('Błąd podczas przesyłania formularzy:', error);
          showNotification('Błąd podczas przesyłania formularzy zamawiającego', 'warning');
        }
      }

      if (specificationFiles.length > 0) {
        try {
          await offerStandaloneService.uploadSpecification(result.id, specificationFiles);
          showNotification('Specyfikacja została przesłana pomyślnie', 'success');
        } catch (error) {
          console.error('Błąd podczas przesyłania specyfikacji:', error);
          showNotification('Błąd podczas przesyłania specyfikacji', 'warning');
        }
      }

      onAdd(result);
      onClose();

    } catch (error: any) {
      console.error('Błąd podczas zapisywania oferty:', error);
      showNotification(
        error.message || `Wystąpił błąd podczas ${isEditMode ? 'aktualizacji' : 'tworzenia'} oferty`, 
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Skrót klawiszowy: § (oraz warianty £/±/IntlBackslash) – przełączanie zakładek formularza
  useEffect(() => {
    if (!open) return;
    const options = { capture: true } as AddEventListenerOptions;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const isShortcut =
        event.code === 'IntlBackslash' || event.key === '§' || event.key === '£' || event.key === '±';
      if (!isShortcut) return;
      // Nie działaj w polach edycyjnych
      const target = event.target as HTMLElement | null;
      const tag = (target?.tagName || '').toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || (target as any)?.isContentEditable;
      if (isEditable) return;
      event.preventDefault();
      event.stopPropagation();
      try {
        (event as any).stopImmediatePropagation?.();
      } catch {}
      const step = event.shiftKey ? -1 : 1;
      const tabsCount = 4; // Podstawowe informacje, Terminy, Wartości finansowe, Linki i dokumenty
      setTabValue(v => (v + step + tabsCount) % tabsCount);
    };
    window.addEventListener('keydown', onKeyDown, options);
    return () => {
      window.removeEventListener('keydown', onKeyDown, options as any);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTabValue(0);
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            {isEditMode ? 'Edytuj ofertę' : 'Dodaj nową ofertę'}
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        <Box sx={{ px: 4, mt: '36px' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="standard"
            sx={{ 
              position: 'relative',
              '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px', bgcolor: '#EDEDED' },
              '& .MuiTabs-scroller': { pl: 0, ml: 0 },
              '& .MuiTabs-flexContainer': { justifyContent: 'space-between' }
            }}
            TabIndicatorProps={{ sx: { height: 2, bgcolor: '#000000' } }}
          >
            <Tab 
              label="Podstawowe informacje" 
              sx={{ 
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                textTransform: 'none',
                color: '#020242',
                minWidth: 'auto',
                maxWidth: 'none',
                px: 0,
                textAlign: 'left',
                '&.Mui-selected': { fontWeight: 500, color: '#020242' }
              }} 
            />
            <Tab 
              label="Terminy" 
              sx={{ 
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                textTransform: 'none',
                color: '#020242',
                minWidth: 'auto',
                maxWidth: 'none',
                px: 0,
                textAlign: 'left',
                '&.Mui-selected': { fontWeight: 500, color: '#020242' }
              }} 
            />
            <Tab 
              label="Wartości finansowe" 
              sx={{ 
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                textTransform: 'none',
                color: '#020242',
                minWidth: 'auto',
                maxWidth: 'none',
                px: 0,
                textAlign: 'left',
                '&.Mui-selected': { fontWeight: 500, color: '#020242' }
              }} 
            />
            <Tab 
              label="Linki i dokumenty" 
              sx={{ 
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                textTransform: 'none',
                color: '#020242',
                minWidth: 'auto',
                maxWidth: 'none',
                px: 0,
                textAlign: 'left',
                '&.Mui-selected': { fontWeight: 500, color: '#020242' }
              }} 
            />
          </Tabs>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', px: 4, py: 3 }}>
          <Box component="form" noValidate>
            <Grid container rowSpacing={1.5} columnSpacing={3}>
            {tabValue === 0 && (
              <>
            <Grid item xs={12} sm={6}>
              <FieldLabel text="Przygotowuje ofertę (PO)" required />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select
                  value={formData.offer_preparer || ''}
                  onChange={(e) => handleInputChange('offer_preparer', e.target.value)}
                  displayEmpty
                  renderValue={(val) => {
                    if (val === 'AI') return 'AI';
                    if (!val) return 'Wybierz z listy';
                    const u = users.find(x => x.user_id === val);
                    return u ? (u.display_name || u.email) : '';
                  }}
                  IconComponent={(iconProps: any) => (
                    <Box
                      component="img"
                      alt="dropdown"
                      src="/assets/icons/keyboard_arrow_down.svg"
                      {...iconProps}
                      sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                    />
                  )}
                  sx={{
                    '& .MuiSelect-select': {
                      color: formData.offer_preparer ? 'inherit' : '#B9B9B9'
                    },
                    '&[aria-expanded="true"]': {
                      '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                    },
                    '&.Mui-expanded': {
                      '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                    }
                  }}
                >
                  {users.map(u => (
                    <MenuItem key={u.user_id} value={u.user_id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22 }} />
                        <Box component="span">{u.display_name || u.email}</Box>
                      </Box>
                    </MenuItem>
                  ))}
                  {/* Dodatkowy wpis – AI jako ostatnia opcja */}
                  <MenuItem value="AI">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src="/assets/ai_badge_unitresco.svg" sx={{ width: 22, height: 22 }} />
                      <Box component="span">AI</Box>
                    </Box>
                  </MenuItem>
                </Select>
                {errors.offer_preparer && (
                  <FormHelperText sx={{ color: '#d32f2f' }}>
                    {errors.offer_preparer}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FieldLabel text="Status" required />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  displayEmpty
                  renderValue={(val) => val || 'Wybierz z listy'}
                  IconComponent={(iconProps: any) => (
                    <Box
                      component="img"
                      alt="dropdown"
                      src="/assets/icons/keyboard_arrow_down.svg"
                      {...iconProps}
                      sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                    />
                  )}
                  sx={{
                    '& .MuiSelect-select': {
                      color: formData.status ? 'inherit' : '#B9B9B9'
                    },
                    '&[aria-expanded="true"]': {
                      '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                    },
                    '&.Mui-expanded': {
                      '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                    }
                  }}
                >
                  {offerStandaloneService.getStatusOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.status && (
                  <FormHelperText sx={{ color: '#d32f2f' }}>
                    {errors.status}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Zamawiający" required />
              <TextField
                fullWidth
                required
                name="client_name"
                value={formData.client_name}
                onChange={(e) => handleInputChange('client_name', e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="Nazwa"
                helperText={errors.client_name}
                error={!!errors.client_name}
              />
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Adres zamawiającego" />
              <TextField
                fullWidth
                name="client_address"
                value={formData.client_address}
                onChange={(e) => handleInputChange('client_address', e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="np. ul. Przykładowa 1, 00-000 Warszawa"
                helperText={errors.client_address}
                error={!!errors.client_address}
              />
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Nazwa zamówienia" required />
              <TextField
                fullWidth
                required
                name="order_name"
                value={formData.order_name}
                onChange={(e) => handleInputChange('order_name', e.target.value)}
                variant="outlined"
                size="small"
                multiline
                rows={2}
                sx={{ 
                  ...textFieldSx, 
                  '& .MuiOutlinedInput-root': { height: 84, backgroundColor: '#fff', borderRadius: '4px', alignItems: 'flex-start' },
                  '& .MuiInputBase-input': { padding: 0 },
                  '& .MuiOutlinedInput-input': { padding: 0 }
                }}
                placeholder="Pełny przedmiot umowy"
                helperText={errors.order_name}
                error={!!errors.order_name}
              />
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Adres inwestycji" />
              <TextField
                fullWidth
                name="investment_address"
                value={formData.investment_address}
                onChange={(e) => handleInputChange('investment_address', e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="np. Al. Jerozolimskie 3, 00-495 Warszawa"
                helperText={errors.investment_address}
                error={!!errors.investment_address}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FieldLabel text="Miejscowość" required />
              <TextField
                fullWidth
                required
                name="location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="Miejscowość"
                helperText={errors.location}
                error={!!errors.location}
              />
            </Grid>
              </>
            )}
            {tabValue === 1 && (
              <>
            <Grid item xs={12} sm={6}>
              <FieldLabel text="Wizja lokalna" required />
              <FormControl fullWidth size="small" sx={selectSx}>
                <Select
                  value={formData.site_inspection}
                  onChange={(e) => handleInputChange('site_inspection', e.target.value as 'Tak' | 'Nie')}
                  displayEmpty
                  renderValue={(val) => val || 'Wybierz z listy'}
                  IconComponent={(iconProps: any) => (
                    <Box
                      component="img"
                      alt="dropdown"
                      src="/assets/icons/keyboard_arrow_down.svg"
                      {...iconProps}
                      sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
                    />
                  )}
                  sx={{
                    '& .MuiSelect-select': {
                      color: formData.site_inspection ? 'inherit' : '#B9B9B9'
                    },
                    '&[aria-expanded="true"]': {
                      '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                    },
                    '&.Mui-expanded': {
                      '& + .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' }
                    }
                  }}
                >
                  {!formData.site_inspection && (
                    <MenuItem value="" disabled>
                      <em>Wybierz z listy</em>
                    </MenuItem>
                  )}
                  {offerStandaloneService.getSiteInspectionOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.site_inspection && (
                  <FormHelperText sx={{ color: '#d32f2f' }}>
                    {errors.site_inspection}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {formData.site_inspection === 'Tak' && (
              <Grid item xs={12} sm={6}>
                <FieldLabel text="Termin wizji lokalnej (TWL)" required />
                <TextField
                  fullWidth
                  required
                  type="date"
                  name="site_inspection_date"
                  value={formData.site_inspection_date}
                  onChange={(e) => handleInputChange('site_inspection_date', e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{
                    ...textFieldSx,
                    '& .MuiInputBase-input': {
                      color: formData.site_inspection_date ? '#2D2D32' : '#B9B9B9'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#B9B9B9',
                      opacity: 1
                    }
                  }}
                  placeholder="rrrr-mm-dd"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <img src="/assets/icons/calendar_today.svg" alt="calendar" style={{ width: 20, height: 20 }} />
                      </InputAdornment>
                    )
                  }}
                  helperText={errors.site_inspection_date}
                  error={!!errors.site_inspection_date}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FieldLabel text="Termin składania oferty (TSO)" required />
              <TextField
                fullWidth
                required
                type="date"
                name="submission_deadline"
                value={formData.submission_deadline}
                onChange={(e) => handleInputChange('submission_deadline', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  ...textFieldSx,
                  '& .MuiInputBase-input': {
                    color: formData.submission_deadline ? '#2D2D32' : '#B9B9B9'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#B9B9B9',
                    opacity: 1
                  }
                }}
                placeholder="rrrr-mm-dd"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <img src="/assets/icons/calendar_today.svg" alt="calendar" style={{ width: 20, height: 20 }} />
                    </InputAdornment>
                  )
                }}
                helperText={errors.submission_deadline}
                error={!!errors.submission_deadline}
              />
            </Grid>
              </>
            )}
            {tabValue === 2 && (
              <>
            <Grid item xs={12} sm={6}>
              <FieldLabel text="Szacunkowa wartość zamówienia (SWZ)" />
              <TextField
                fullWidth
                type="number"
                name="estimated_order_value"
                value={formData.estimated_order_value || ''}
                onChange={(e) => handleInputChange('estimated_order_value', e.target.value ? parseFloat(e.target.value) : undefined)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="np. 1500000,00"
                InputProps={{
                  endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                }}
                helperText={errors.estimated_order_value}
                error={!!errors.estimated_order_value}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FieldLabel text="Wadium" required />
              <TextField
                fullWidth
                required
                type="number"
                name="bid_bond"
                value={formData.bid_bond || ''}
                onChange={(e) => handleInputChange('bid_bond', e.target.value ? parseFloat(e.target.value) : undefined)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="np. 1000000,00"
                InputProps={{
                  endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                }}
                helperText={errors.bid_bond}
                error={!!errors.bid_bond}
              />
            </Grid>
              </>
            )}
            {tabValue === 3 && (
              <>
            <Grid item xs={12}>
              <FieldLabel text="Link do Google Drive" />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  name="googleDriveLink"
                  value={googleDriveLink}
                  onChange={(e) => setGoogleDriveLink(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={textFieldSx}
                  placeholder="Wpisz adres url"
                  disabled={!isEditMode}
                  helperText={isEditMode && !googleDriveLink ? "Kliknij 'Generuj folder' aby utworzyć folder Google Drive" : ""}
                />
                {isEditMode && !googleDriveLink && (
                  <Button
                    variant="contained"
                    onClick={handleGenerateFolder}
                    disabled={generatingFolder || !formData.client_name || !formData.order_name}
                    startIcon={generatingFolder ? <CircularProgress size={16} /> : <RefreshIcon />}
                    sx={{ 
                      bgcolor: '#004273',
                      '&:hover': { bgcolor: '#003660' },
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {generatingFolder ? 'Tworzę...' : 'Generuj folder'}
                  </Button>
                )}
                {googleDriveLink && (
                  <IconButton
                    component="a"
                    href={googleDriveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    title="Otwórz w Google Drive"
                  >
                    <LinkIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Link do Collectifile" />
              <TextField
                fullWidth
                name="miro_link"
                value={formData.miro_link}
                onChange={(e) => handleInputChange('miro_link', e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="Wpisz adres url"
              />
            </Grid>

            <Grid item xs={12}>
              <FieldLabel text="Strona zamawiającego" required />
              <TextField
                fullWidth
                required
                name="client_website"
                value={formData.client_website}
                onChange={(e) => handleInputChange('client_website', e.target.value)}
                variant="outlined"
                size="small"
                sx={textFieldSx}
                placeholder="Wpisz adres url"
                helperText={errors.client_website}
                error={!!errors.client_website}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Doświadczenie techniczne" required />
              <TextField
                fullWidth
                required
                name="technical_experience"
                value={formData.technical_experience}
                onChange={(e) => handleInputChange('technical_experience', e.target.value)}
                variant="outlined"
                size="small"
                multiline
                rows={5}
                sx={{ 
                  ...textFieldSx, 
                  '& .MuiOutlinedInput-root': { 
                    height: 'auto', 
                    backgroundColor: '#fff', 
                    borderRadius: '4px', 
                    alignItems: 'flex-start' 
                  },
                  '& .MuiInputBase-input': { 
                    padding: 0 
                  },
                  '& .MuiOutlinedInput-input': { 
                    padding: 0 
                  }
                }}
                placeholder="Wpisz opis"
                helperText={errors.technical_experience}
                error={!!errors.technical_experience}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Potencjał osobowy" required />
              <TextField
                fullWidth
                required
                name="personnel_potential"
                value={formData.personnel_potential}
                onChange={(e) => handleInputChange('personnel_potential', e.target.value)}
                variant="outlined"
                size="small"
                multiline
                rows={5}
                sx={{ 
                  ...textFieldSx, 
                  '& .MuiOutlinedInput-root': { 
                    height: 'auto', 
                    backgroundColor: '#fff', 
                    borderRadius: '4px', 
                    alignItems: 'flex-start' 
                  },
                  '& .MuiInputBase-input': { 
                    padding: 0 
                  },
                  '& .MuiOutlinedInput-input': { 
                    padding: 0 
                  }
                }}
                placeholder="Wpisz opis"
                helperText={errors.personnel_potential}
                error={!!errors.personnel_potential}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Sytuacja ekonomiczna" required />
              <TextField
                fullWidth
                required
                name="economic_situation"
                value={formData.economic_situation}
                onChange={(e) => handleInputChange('economic_situation', e.target.value)}
                variant="outlined"
                size="small"
                multiline
                rows={5}
                sx={{ 
                  ...textFieldSx, 
                  '& .MuiOutlinedInput-root': { 
                    height: 'auto', 
                    backgroundColor: '#fff', 
                    borderRadius: '4px', 
                    alignItems: 'flex-start' 
                  },
                  '& .MuiInputBase-input': { 
                    padding: 0 
                  },
                  '& .MuiOutlinedInput-input': { 
                    padding: 0 
                  }
                }}
                placeholder="Wpisz opis"
                helperText={errors.economic_situation}
                error={!!errors.economic_situation}
              />
            </Grid>
            {!isEditMode && (
              <>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Formularze zamawiającego
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Wybierz pliki
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={(e) => handleFileChange('client-forms', e.target.files)}
                      />
                    </Button>
                    {clientFormsFiles.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Wybrane pliki: {clientFormsFiles.length}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Specyfikacja zamówienia
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ mt: 1 }}
                    >
                      Wybierz pliki
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={(e) => handleFileChange('specification', e.target.files)}
                      />
                    </Button>
                    {specificationFiles.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Wybrane pliki: {specificationFiles.length}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </>
            )}
              </>
            )}
          </Grid>
        </Box>
        </Box>
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
          <Box />
          <Box>
            <Button 
              onClick={onClose} 
              variant="text"
              disabled={loading}
              sx={{ 
                fontFamily: 'Roboto, sans-serif', 
                fontWeight: 500, 
                fontSize: 14, 
                color: '#2D2D32', 
                textTransform: 'none' 
              }}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              sx={{ 
                backgroundColor: '#020242',
                '&:hover': {
                  backgroundColor: '#030360'
                }
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" />
                </>
              ) : (
                isEditMode ? 'Aktualizuj' : 'Utwórz'
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddOfferStandaloneForm;
